'use client'

import { useState, useEffect } from 'react'
import { m, AnimatePresence } from 'framer-motion'
import { RefreshCw, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { useVersionCheck } from '@/hooks/use-version-check'

const DISMISSAL_KEY = 'version-banner-dismissed'

interface VersionUpdateBannerProps {
  initialVersion: string
}

export function VersionUpdateBanner({ initialVersion }: VersionUpdateBannerProps) {
  const { hasNewVersion, refresh } = useVersionCheck(initialVersion)
  const [isDismissed, setIsDismissed] = useState(false)

  // Check if banner was dismissed in this session
  /* eslint-disable react-hooks/set-state-in-effect -- reading client-only sessionStorage on mount */
  useEffect(() => {
    const dismissed = sessionStorage.getItem(DISMISSAL_KEY)
    if (dismissed === 'true') {
      setIsDismissed(true)
    }
  }, [])
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleDismiss = () => {
    setIsDismissed(true)
    sessionStorage.setItem(DISMISSAL_KEY, 'true')
  }

  const shouldShow = hasNewVersion && !isDismissed

  return (
    <AnimatePresence>
      {shouldShow && (
        <m.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3, ease: 'easeOut' }}
          className="fixed bottom-0 left-0 right-0 z-40 bg-zinc-900/95 backdrop-blur-sm border-t border-zinc-800"
        >
          <div className="container mx-auto px-4 py-2">
            <div className="flex items-center justify-between gap-4">
              {/* Left side: Icon + Message */}
              <div className="flex items-center gap-3">
                <RefreshCw className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                <span className="text-sm text-zinc-400">
                  New version available
                </span>
              </div>

              {/* Right side: Refresh button + Dismiss */}
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={refresh}
                  className="h-8 text-xs border-zinc-700 hover:border-zinc-600 hover:bg-zinc-800"
                >
                  Refresh Now
                </Button>
                <button
                  onClick={handleDismiss}
                  className="p-1 rounded hover:bg-zinc-800 text-zinc-500 hover:text-zinc-300 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </m.div>
      )}
    </AnimatePresence>
  )
}

